import type { FastDelivery, FastDeliveryProduction } from '../../../app/fast-deliveries'
import type { PjshellId } from '../../../copy/pjshell'
import type { StepState } from './ui'

export type DeliveryStatus = {
  copyId: PjshellId
  tone: 'ok' | 'warn' | 'bad' | 'neutral'
}

export type DeliverySummary = {
  copyId: PjshellId
  text: string | null
}

export const PUBLISH_STEPS = [
  'accepted',
  'artifact_ready',
  'main_updated',
  'deploying',
  'production_verified',
] as const

export function fastDeliveryStatus(delivery: FastDelivery): DeliveryStatus {
  if (delivery.phase === 'needs_rebase') {
    return { copyId: 'pjshell.release.state.needs_rebase', tone: 'warn' }
  }
  if (delivery.lineage_relation === 'missing' || delivery.phase === 'main_missing') {
    if (delivery.phase === 'accepted' || delivery.phase === 'building' || delivery.phase === 'artifact_ready') {
      return statusForPhase(delivery)
    }
    return { copyId: 'pjshell.release.state.missing', tone: 'bad' }
  }
  return statusForPhase(delivery)
}

const phaseOwnedSummary = new Set<FastDelivery['phase']>([
  'building',
  'artifact_ready',
  'main_updated',
  'deploying',
  'deployed',
  'production_verified',
  'delivered_in_successor',
])

const staleStatusMessagePattern = /受影响服务镜像已就绪|尚未更新\s*main/i

/** 发布阶段是当前事实；后台留下的早期 status_message 不能覆盖阶段摘要。 */
export function fastDeliverySummary(delivery: FastDelivery): DeliverySummary {
  const copyId = fastDeliveryStatus(delivery).copyId
  const message = delivery.status_message?.trim() ?? ''
  if (!message || (phaseOwnedSummary.has(delivery.phase) && staleStatusMessagePattern.test(message))) {
    return { copyId, text: null }
  }
  const text = message
  return { copyId, text: text || null }
}

function statusForPhase(delivery: FastDelivery): DeliveryStatus {
  if (delivery.phase === 'accepted') {
    return { copyId: 'pjshell.release.state.accepted', tone: 'neutral' }
  }
  if (delivery.phase === 'candidate_ready') {
    // 候选态：中性，绝不用生产绿；车道徽标另标「快送候选」。
    return { copyId: 'pjshell.release.state.candidate_ready', tone: 'neutral' }
  }
  if (delivery.phase === 'building') {
    return { copyId: 'pjshell.release.state.building', tone: 'warn' }
  }
  if (delivery.phase === 'artifact_ready') {
    return { copyId: 'pjshell.release.state.artifact_ready', tone: 'warn' }
  }
  if (delivery.phase === 'main_updated') {
    if (delivery.lineage_relation === 'included_in_newer' || delivery.coalesced_into) {
      // 进了更新 main，但仍不是已验证生产。
      return { copyId: 'pjshell.release.state.included', tone: 'neutral' }
    }
    if (visibleAffectedServices(delivery.affected_services).length === 0) {
      return { copyId: 'pjshell.release.state.exact', tone: 'neutral' }
    }
    return { copyId: 'pjshell.release.state.going_live', tone: 'warn' }
  }
  if (delivery.phase === 'deploying') {
    return { copyId: 'pjshell.release.state.going_live', tone: 'warn' }
  }
  if (delivery.phase === 'deployed') {
    return { copyId: 'pjshell.release.state.deployed', tone: 'warn' }
  }
  if (delivery.phase === 'production_verified') {
    if (delivery.coalesced_into) {
      return { copyId: 'pjshell.release.state.included', tone: 'neutral' }
    }
    return { copyId: 'pjshell.release.state.production_verified', tone: 'ok' }
  }
  if (delivery.phase === 'delivered_in_successor') {
    return { copyId: 'pjshell.release.state.delivered_in_successor', tone: 'ok' }
  }
  if (delivery.phase === 'rolled_back') {
    return { copyId: 'pjshell.release.state.rolled_back', tone: 'neutral' }
  }
  return { copyId: 'pjshell.release.state.failed', tone: 'bad' }
}

export function showDeliveryFailure(delivery: FastDelivery): boolean {
  if (delivery.phase === 'failed' || delivery.phase === 'needs_rebase') return true
  if (delivery.phase === 'accepted' || delivery.phase === 'building' || delivery.phase === 'artifact_ready') {
    return false
  }
  return delivery.lineage_relation === 'missing' || delivery.phase === 'main_missing'
}

export function failureStageCopy(delivery: FastDelivery): PjshellId | null {
  if (delivery.phase === 'needs_rebase') return 'pjshell.release.failure.stage.rebase'
  if (
    delivery.phase === 'accepted' ||
    delivery.phase === 'building' ||
    delivery.phase === 'artifact_ready' ||
    delivery.phase === 'candidate_ready'
  ) {
    return null
  }
  if (delivery.ledger_state === 'build_failed' || delivery.repo_gate_state === 'build_failed') {
    return 'pjshell.release.failure.stage.build'
  }
  if (delivery.lineage_relation === 'missing' || delivery.phase === 'main_missing') {
    return 'pjshell.release.failure.stage.main'
  }
  if (delivery.health_ok === false || delivery.smoke_ok === false) {
    return 'pjshell.release.failure.stage.verification'
  }
  if (delivery.phase === 'failed' && !delivery.deployment_sha) {
    return 'pjshell.release.failure.stage.deploy'
  }
  return null
}

export function isBootstrapAdminRecovery(delivery: FastDelivery): boolean {
  return delivery.affected_services.includes('bootstrap_required')
}

export function visibleAffectedServices(services: string[] | null | undefined): string[] {
  return (services ?? []).filter((service) => {
    const name = service.trim()
    return name !== '' && name !== 'bootstrap_required'
  })
}

export function failureActionCopy(delivery: FastDelivery): PjshellId {
  if (isBootstrapAdminRecovery(delivery)) {
    return 'pjshell.release.failure.action.bootstrap'
  }
  if (delivery.phase === 'needs_rebase') return 'pjshell.release.failure.action.rebase'
  if (delivery.ledger_state === 'build_failed' || delivery.repo_gate_state === 'build_failed') {
    return 'pjshell.release.failure.action.build'
  }
  if (delivery.health_ok === false || delivery.smoke_ok === false) {
    return 'pjshell.release.failure.action.verify'
  }
  if (delivery.lineage_relation === 'missing' || delivery.phase === 'main_missing') {
    return 'pjshell.release.failure.action.main'
  }
  return 'pjshell.release.failure.action.generic'
}

export function failureFile(delivery: FastDelivery): string | null {
  const code = delivery.failure_code?.trim() ?? ''
  if (code.includes('/') && !code.includes(' ')) return code
  return null
}

export function failureReason(delivery: FastDelivery): string | null {
  const summary = delivery.failure_summary?.trim() ?? ''
  if (summary) return summary
  const message = delivery.status_message?.trim() ?? ''
  return message || null
}

export function deliveryRollbackSha(delivery: FastDelivery): string | null {
  const sha = (delivery as FastDelivery & { rollback_sha?: string }).rollback_sha?.trim()
  return sha || null
}

export function deliveryReceipt(delivery: FastDelivery): string | null {
  const digest = delivery.receipt_digest?.trim() ?? ''
  return digest || null
}

/** 列表信封没有顶层 current_main_sha：从各条交付里取最近更新且非空的那条。 */
export function currentMainShaFromDeliveries(deliveries: FastDelivery[]): string | null {
  let bestSha: string | null = null
  let bestUpdated = Number.NEGATIVE_INFINITY
  for (const delivery of deliveries) {
    const sha = delivery.current_main_sha?.trim() ?? ''
    if (!sha) continue
    const updated = Date.parse(delivery.updated_at)
    if (!Number.isFinite(updated)) continue
    if (updated > bestUpdated) {
      bestUpdated = updated
      bestSha = sha
    }
  }
  return bestSha
}

export function mainMatchesProduction(
  mainSha: string | null,
  productionSha: string | null,
): 'match' | 'mismatch' | 'unknown' {
  if (!mainSha || !productionSha) return 'unknown'
  return mainSha === productionSha ? 'match' : 'mismatch'
}

export type DeliveryActivityLane = 'attention' | 'active' | 'settled'

export type DeliveryActivityContext = {
  production?: FastDeliveryProduction | null
}

function trimSha(value: string | null | undefined): string {
  return value?.trim() ?? ''
}

/** infra-fast / dev-fastlane / bootstrap 合成发布号——API 已从生产基线过滤，UI 也不得穿生产绿衣。 */
export function isInfraFastReleaseId(value: string | null | undefined): boolean {
  const id = (value ?? '').trim()
  return /^(infra-fast-|dev-fastlane-|bootstrap-)/.test(id)
}

/** 只有未合流的 production_verified 才是已验证生产。 */
export function isVerifiedProductionDelivery(delivery: FastDelivery): boolean {
  return delivery.phase === 'production_verified' && !trimSha(delivery.coalesced_into)
}

export type DeliveryLaneKind = 'verified_production' | 'fast_candidate' | 'other'

/**
 * 视觉车道：已验证生产 vs 快送候选。
 * 候选包含 candidate_ready / 未验证的 main_updated / 合成 infra-fast 号。
 */
export function deliveryLaneKind(delivery: FastDelivery): DeliveryLaneKind {
  if (isVerifiedProductionDelivery(delivery)) return 'verified_production'
  if (delivery.phase === 'delivered_in_successor') return 'other'
  if (
    delivery.phase === 'candidate_ready' ||
    delivery.phase === 'accepted' ||
    delivery.phase === 'building' ||
    delivery.phase === 'artifact_ready' ||
    delivery.phase === 'main_updated' ||
    delivery.phase === 'deploying' ||
    delivery.phase === 'deployed' ||
    isInfraFastReleaseId(delivery.deployment_release_id) ||
    isInfraFastReleaseId(delivery.release_id)
  ) {
    return 'fast_candidate'
  }
  return 'other'
}

/** 失败 / 丢 main / 需重整：需要人看，不算正在上线。 */
export function deliveryNeedsAttention(delivery: FastDelivery): boolean {
  if (
    delivery.phase === 'failed' ||
    delivery.phase === 'needs_rebase' ||
    delivery.phase === 'main_missing'
  ) {
    return true
  }
  if (
    delivery.phase === 'accepted' ||
    delivery.phase === 'candidate_ready' ||
    delivery.phase === 'building' ||
    delivery.phase === 'artifact_ready'
  ) {
    return false
  }
  return delivery.lineage_relation === 'missing'
}

/**
 * 有明确覆盖证据才算收口：进入更新 main、coalesced、或被当前/上一版生产基线覆盖。
 * 不把 main_updated 当成 deployed，也不用超时猜测。
 */
export function deliveryHasCoveringEvidence(
  delivery: FastDelivery,
  context: DeliveryActivityContext = {},
): boolean {
  if (
    delivery.phase === 'production_verified' ||
    delivery.phase === 'rolled_back' ||
    delivery.phase === 'delivered_in_successor'
  ) {
    return true
  }
  if (delivery.phase === 'main_updated' && visibleAffectedServices(delivery.affected_services).length === 0) {
    return true
  }
  if (trimSha(delivery.coalesced_into)) return true
  if (delivery.lineage_relation === 'included_in_newer') return true
  if (delivery.ledger_state === 'coalesced_into' || delivery.repo_gate_state === 'coalesced_into') {
    return true
  }

  const production = context.production
  const productionSha = trimSha(production?.sha)
  if (!productionSha) return false

  const candidate = trimSha(delivery.candidate_sha)
  const accepted = trimSha(delivery.accepted_main_sha)
  const currentMain = trimSha(delivery.current_main_sha)
  const coalesced = trimSha(delivery.coalesced_into)
  const deployment = trimSha(delivery.deployment_sha)
  const previous = trimSha(production?.previous_sha)

  // 这条候选本身就是当前生产基线（即便 phase 还没跟上）。
  if (candidate === productionSha || accepted === productionSha) return true

  // 被当前生产 tip 包含（与后端 OverlayProductionBaseline 的 containedByLive 对齐）。
  if (coalesced === productionSha && currentMain === productionSha) return true

  // 生产已经前进：我们是 previous_sha，被更新生产覆盖。
  if (
    previous &&
    (candidate === previous || accepted === previous || deployment === previous)
  ) {
    return true
  }

  return false
}

export function deliveryActivityLane(
  delivery: FastDelivery,
  context: DeliveryActivityContext = {},
): DeliveryActivityLane {
  if (deliveryNeedsAttention(delivery)) return 'attention'
  if (deliveryHasCoveringEvidence(delivery, context)) return 'settled'
  return 'active'
}

/** 详情时钟 / 总耗时终态：只有后端终态或无需运行时部署的 main_updated 才冻结。 */
export function isFastDeliveryTerminal(
  delivery: FastDelivery,
  _context: DeliveryActivityContext = {},
): boolean {
  if (deliveryNeedsAttention(delivery)) return true
  if (
    delivery.phase === 'production_verified' ||
    delivery.phase === 'delivered_in_successor' ||
    delivery.phase === 'rolled_back'
  ) {
    return true
  }
  return isSettledMainUpdated(delivery)
}

/** 无运行时服务的 main_updated 已在进入 main 时收口，无需展示部署与生产验证。 */
export function isSettledMainUpdated(
  delivery: FastDelivery,
  _context: DeliveryActivityContext = {},
): boolean {
  return (
    delivery.phase === 'main_updated' &&
    failureStageCopy(delivery) === null &&
    visibleAffectedServices(delivery.affected_services).length === 0
  )
}

export function partitionDeliveriesByActivity(
  deliveries: FastDelivery[],
  production: FastDeliveryProduction | null = null,
): {
  attention: FastDelivery[]
  active: FastDelivery[]
  settled: FastDelivery[]
} {
  const attention: FastDelivery[] = []
  const active: FastDelivery[] = []
  const settled: FastDelivery[] = []
  const context: DeliveryActivityContext = { production }
  for (const item of deliveries) {
    const lane = deliveryActivityLane(item, context)
    if (lane === 'attention') attention.push(item)
    else if (lane === 'active') active.push(item)
    else settled.push(item)
  }
  return { attention, active, settled }
}

export function applicationSteps(delivery: FastDelivery): Array<{ title: PjshellId; state: StepState }> {
  const phase = delivery.phase
  const failStage = failureStageCopy(delivery)
  const closedAtMain = isSettledMainUpdated(delivery)
  const accepted: StepState =
    phase === 'accepted' || phase === 'candidate_ready' ? 'now' : 'done'
  const artifact: StepState =
    failStage === 'pjshell.release.failure.stage.build'
      ? 'fail'
      : phase === 'artifact_ready' || phase === 'needs_rebase'
        ? phase === 'needs_rebase'
          ? 'done'
          : 'now'
        : phase === 'building'
          ? 'now'
          : phase === 'accepted' || phase === 'candidate_ready'
            ? 'wait'
            : 'done'
  const main: StepState =
    failStage === 'pjshell.release.failure.stage.main' ||
    failStage === 'pjshell.release.failure.stage.rebase' ||
    phase === 'main_missing' ||
    phase === 'needs_rebase'
      ? 'fail'
      : phase === 'main_updated'
        ? closedAtMain
          ? 'done'
          : 'now'
        : phase === 'deploying' ||
            phase === 'deployed' ||
            phase === 'production_verified' ||
            phase === 'delivered_in_successor' ||
            phase === 'rolled_back' ||
            failStage === 'pjshell.release.failure.stage.verification' ||
            failStage === 'pjshell.release.failure.stage.deploy'
          ? 'done'
          : 'wait'
  const deploy: StepState =
    failStage === 'pjshell.release.failure.stage.deploy'
      ? 'fail'
      : phase === 'deploying' || phase === 'deployed'
        ? 'now'
        : phase === 'production_verified' ||
            phase === 'delivered_in_successor' ||
            phase === 'rolled_back' ||
            failStage === 'pjshell.release.failure.stage.verification'
          ? 'done'
          : 'wait'
  const verify: StepState =
    failStage === 'pjshell.release.failure.stage.verification' || phase === 'rolled_back'
      ? 'fail'
      : phase === 'deployed'
        ? 'now'
        : phase === 'production_verified' || phase === 'delivered_in_successor'
          ? 'done'
          : 'wait'
  const steps: Array<{ title: PjshellId; state: StepState }> = [
    { title: 'pjshell.release.step.accepted', state: accepted },
    { title: 'pjshell.release.step.artifact_ready', state: artifact },
    { title: 'pjshell.release.step.main', state: main },
    { title: 'pjshell.release.step.deploy_app', state: deploy },
    { title: 'pjshell.release.step.production_verify', state: verify },
  ]
  return closedAtMain ? steps.slice(0, 3) : steps
}

/** 生产证据三元组状态：已确认 / 未核验 / 未提供。只映射已有字段，不发明新证据。 */
export type EvidenceTriadState = 'confirmed' | 'unverified' | 'absent'

export type EvidenceTriad = {
  receipt: EvidenceTriadState
  deployment: EvidenceTriadState
  online: EvidenceTriadState
}

/**
 * 制品回执 · 部署提交 · 线上业务。
 * - receipt_digest / deployment_sha：有值=已确认，否则未提供
 * - 线上业务：production_verified=已确认；health/smoke 曾失败或只过了部分=未核验；全空=未提供
 */
export function evidenceTriad(delivery: FastDelivery): EvidenceTriad {
  const receipt: EvidenceTriadState = deliveryReceipt(delivery) ? 'confirmed' : 'absent'
  const deployment: EvidenceTriadState = trimSha(delivery.deployment_sha) ? 'confirmed' : 'absent'

  let online: EvidenceTriadState = 'absent'
  if (isVerifiedProductionDelivery(delivery) || Boolean(delivery.production_verified_at?.trim())) {
    online = 'confirmed'
  } else if (delivery.health_ok === false || delivery.smoke_ok === false) {
    online = 'unverified'
  } else if (delivery.health_ok === true || delivery.smoke_ok === true) {
    // 单项/双项检查有结果，但尚未 production_verified —— 不能冒充已确认。
    online = 'unverified'
  }

  return { receipt, deployment, online }
}
